import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import api from '../../services/api'
import { translateApiError } from '../../utils/errorMessages'
import { Button, FormGroup, Input, Select } from '../../components/ui'
import ConfirmModal from '../../components/ConfirmModal'
import EntityHistory from '../../components/EntityHistory'
import './Admin.css'

const emptyForm = {
  login: '',
  password: '',
  role: 'employee',
  employee_id: '',
  is_active: true,
  department_ids: []
}

function AdminUserFormPage() {
  const { id } = useParams()
  const isCreate = !id
  const navigate = useNavigate()
  const { user: currentUser, fetchUser } = useAuth()

  const [editingUser, setEditingUser] = useState(null)
  const [formData, setFormData] = useState(emptyForm)
  const [loadError, setLoadError] = useState(null)
  const [loadingUser, setLoadingUser] = useState(!isCreate)
  const [employees, setEmployees] = useState([])
  const [departments, setDepartments] = useState([])
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteError, setDeleteError] = useState(null)

  usePageHeader({
    backLink: '/admin/users',
    backLabel: '← К списку',
    title: isCreate ? 'Создание пользователя' : 'Редактирование пользователя'
  }, [isCreate])

  useEffect(() => {
    fetchEmployees()
    fetchDepartments()
  }, [])

  useEffect(() => {
    if (isCreate) {
      setEditingUser(null)
      setFormData(emptyForm)
      setLoadError(null)
      setLoadingUser(false)
      return
    }

    let cancelled = false
    setLoadingUser(true)
    setLoadError(null)
    api.get('/admin/users')
      .then((res) => {
        if (cancelled) return
        const u = res.data?.users?.find((x) => x.id === id)
        if (!u) {
          setLoadError('Пользователь не найден')
          setEditingUser(null)
          return
        }
        setEditingUser(u)
        setFormData({
          login: u.login,
          password: '',
          role: u.role,
          employee_id: u.employee_id || '',
          is_active: u.is_active,
          department_ids: u.department_ids || []
        })
      })
      .catch(() => {
        if (!cancelled) setLoadError('Не удалось загрузить пользователя')
      })
      .finally(() => {
        if (!cancelled) setLoadingUser(false)
      })

    return () => { cancelled = true }
  }, [id, isCreate])

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/admin/employees')
      if (response.data?.employees) setEmployees(response.data.employees)
    } catch (error) {
      console.error('Error fetching employees:', error)
    }
  }

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/company/departments')
      if (response.data?.departments) setDepartments(response.data.departments)
    } catch (error) {
      console.error('Error fetching departments:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editingUser) {
        const updateData = {
          login: formData.login,
          role: formData.role,
          is_active: formData.is_active,
          employee_id: formData.employee_id || null
        }
        if (formData.password) updateData.password = formData.password
        if (formData.role === 'department_head') {
          updateData.department_ids = formData.department_ids || []
        }
        await api.put(`/admin/users/${editingUser.id}`, updateData)
        alert('Пользователь успешно обновлен!')
        const wasSelf = editingUser.id === currentUser?.id
        navigate('/admin/users')
        if (wasSelf) {
          await fetchUser()
          alert('Ваши права доступа были обновлены. Если вы были назначены руководителем подразделения, перелогиньтесь для полного обновления токена.')
        }
      } else {
        const createData = {
          ...formData,
          employee_id: formData.employee_id || null
        }
        if (formData.role === 'department_head') {
          createData.department_ids = formData.department_ids || []
        }
        await api.post('/admin/users', createData)
        alert('Пользователь успешно создан!')
        navigate('/admin/users')
      }
    } catch (error) {
      console.error('Error saving user:', error)
      alert('Ошибка сохранения пользователя: ' + translateApiError(error.response?.data?.error || error.message))
    }
  }

  const handleToggleActive = async () => {
    if (!editingUser) return
    const action = editingUser.is_active ? 'деактивировать' : 'активировать'
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} пользователя?`)) return
    try {
      await api.put(`/admin/users/${editingUser.id}`, { is_active: !editingUser.is_active })
      navigate('/admin/users')
    } catch (error) {
      alert('Ошибка изменения статуса пользователя: ' + translateApiError(error.response?.data?.error || error.message))
    }
  }

  const handleDeleteConfirm = async () => {
    if (!editingUser) return
    try {
      setDeleteError(null)
      await api.delete(`/admin/users/${editingUser.id}`)
      setDeleteModal(false)
      navigate('/admin/users')
    } catch (error) {
      setDeleteError(translateApiError(error.response?.data?.error || error.message))
    }
  }

  if (!isCreate && loadingUser) {
    return <div className="admin-page">Загрузка...</div>
  }

  if (!isCreate && loadError) {
    return (
      <div className="admin-page">
        <p>{loadError}</p>
        <Button variant="secondary" onClick={() => navigate('/admin/users')}>К списку</Button>
      </div>
    )
  }

  return (
    <div className="admin-page">
      <div className="user-form-card">
        <form onSubmit={handleSubmit} className="user-form">
          <h2>{isCreate ? 'Создание пользователя' : 'Редактирование пользователя'}</h2>
          <div className="form-fields">
            <FormGroup label="Логин" required>
              <Input
                type="text"
                value={formData.login}
                onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                required
              />
            </FormGroup>
            <FormGroup label={editingUser ? 'Пароль (оставьте пустым, чтобы не менять)' : 'Пароль'} required={!editingUser}>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
              />
            </FormGroup>
            <FormGroup label="Роль">
              <Select
                value={formData.role}
                onChange={(e) => {
                  const newRole = e.target.value
                  setFormData({
                    ...formData,
                    role: newRole,
                    department_ids: newRole === 'department_head' ? formData.department_ids : []
                  })
                }}
              >
                <option value="employee">Сотрудник</option>
                <option value="department_head">Руководитель подразделения</option>
                <option value="accountant">Бухгалтер</option>
                <option value="management">Руководство</option>
                <option value="admin">Администратор</option>
              </Select>
            </FormGroup>
            {formData.role === 'department_head' && (
              <FormGroup label="Подразделения (можно выбрать несколько)">
                <Select
                  multiple
                  size={Math.min(departments.length, 5)}
                  value={formData.department_ids}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (option) => option.value)
                    setFormData({ ...formData, department_ids: selected })
                  }}
                  style={{ minHeight: '100px' }}
                >
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </Select>
                <small style={{ display: 'block', marginTop: '4px', color: 'var(--color-text-secondary)' }}>
                  Удерживайте Ctrl (Cmd на Mac) для выбора нескольких подразделений
                </small>
              </FormGroup>
            )}
            <FormGroup label="Сотрудник (необязательно)">
              <Select
                value={formData.employee_id || ''}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
              >
                <option value="">— Не выбран —</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.full_name} {emp.department ? `(${emp.department})` : ''}
                  </option>
                ))}
              </Select>
            </FormGroup>
            {editingUser && (
              <FormGroup label="">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <label htmlFor="is_active">Активен</label>
                </div>
              </FormGroup>
            )}
          </div>
          <div className="form-actions">
            <div className="button-group">
              <Button type="button" variant="secondary" onClick={() => navigate('/admin/users')}>
                Отмена
              </Button>
              <Button type="submit" variant="primary">
                {editingUser ? 'Сохранить' : 'Создать'}
              </Button>
            </div>
          </div>
        </form>

        {editingUser?.id && (
          <>
            <div className="user-form-danger-zone" style={{ marginTop: 'var(--spacing-xl)', paddingTop: 'var(--spacing-lg)', borderTop: '1px solid var(--color-border)' }}>
              <h3 style={{ margin: '0 0 var(--spacing-md) 0', fontSize: '1rem', fontWeight: 600 }}>Опасная зона</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-md)' }}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleToggleActive}
                  className={editingUser.is_active ? 'btn-toggle-inactive' : 'btn-toggle-active'}
                >
                  {editingUser.is_active ? 'Деактивировать' : 'Активировать'}
                </Button>
                <Button type="button" variant="danger" onClick={() => setDeleteModal(true)}>
                  Удалить навсегда
                </Button>
              </div>
            </div>
            <EntityHistory entityType="users" entityId={editingUser.id} />
          </>
        )}
      </div>

      {deleteModal && editingUser && (
        <ConfirmModal
          title="Подтверждение удаления"
          message={
            <p>
              Вы уверены, что хотите удалить пользователя <strong>{editingUser.login}</strong>?
              Это действие нельзя отменить.
            </p>
          }
          confirmLabel="Удалить навсегда"
          cancelLabel="Отмена"
          confirmVariant="danger"
          onConfirm={handleDeleteConfirm}
          onCancel={() => { setDeleteModal(false); setDeleteError(null) }}
          error={deleteError}
        />
      )}
    </div>
  )
}

export default AdminUserFormPage
